import { useState, useEffect } from 'react';
import { LocalizationProvider, DateCalendar, PickersDay, PickersDayProps } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import i18n from 'i18next';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/ro';
import { Tooltip, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

const localeMap = {
  'en-GB': 'en-gb',
  'de-DE': 'de',
  'es-ES': 'es',
  'fr-FR': 'fr',
  'it-IT': 'it',
  'ro-RO': 'ro',
  'pl-PL': 'pl',
};

type Language = keyof typeof localeMap;

const setDayjsLocale = (language: Language): string => {
  const dayjsLocale = localeMap[language] ?? 'en-gb';
  dayjs.locale(dayjsLocale);
  return dayjsLocale;
};

const setDayjsLocaleFromLocalStorage = (): string => {
  const storedLanguage = (localStorage.getItem('i18nextLng') ?? 'en-GB') as Language;
  const dayjsLocale = localeMap[storedLanguage] ?? 'en-gb';
  dayjs.locale(dayjsLocale);
  return dayjsLocale;
};

export interface ODSAvailability {
  date: string;
  availableStores: number;
  totalStores: number;
}

interface BulkOrderDateCalendarProps {
  value: Dayjs | null;
  onChange: (date: Dayjs | null) => void;
  disabled?: boolean;
  disableHighlightToday?: boolean;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  odsAvailability: Record<string, ODSAvailability>;
}

const BulkOrderDateCalendar: React.FC<BulkOrderDateCalendarProps> = ({
  value,
  onChange,
  disabled,
  disableHighlightToday,
  minDate,
  maxDate,
  odsAvailability,
}) => {
  const [locale, setLocale] = useState<string>(setDayjsLocaleFromLocalStorage());
  const theme = useTheme();
  const { t } = useTranslation('bulkOrderManagement');

  useEffect(() => {
    const currentLanguage = i18n.language as Language;
    setDayjsLocale(currentLanguage);
    setLocale(localeMap[currentLanguage] ?? 'en-gb');

    const handleLanguageChange = (lng: Language) => {
      const newLocale = setDayjsLocale(lng);
      setLocale(newLocale);
    };

    i18n.on('languageChanged', handleLanguageChange);
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, []);

  const CustomDay = (props: PickersDayProps<Dayjs>) => {
    const { day, ...other } = props;
    const dateString = day.format('YYYY-MM-DD');
    const availability = odsAvailability?.[dateString];

    if (!availability) {
      return <PickersDay {...other}
        day={day} />;
    }

    const coveragePercentage = availability.totalStores > 0
      ? Math.round((availability.availableStores / availability.totalStores) * 100)
      : 0;
    const isFullCoverage = coveragePercentage === 100;
    const isSelected = value?.isSame(day, 'day');
    const tooltipText = isFullCoverage
      ? t('odsTooltipAllStores')
      : t('odsTooltipStoresAvailable', {
        availableStores: availability.availableStores,
        totalStores: availability.totalStores,
      });

    return (
      <Tooltip title={tooltipText}
        arrow>
        <PickersDay
          {...other}
          day={day}
          sx={{
            ...other.sx,
            ...(isFullCoverage && {
              backgroundColor: isSelected ? theme.palette.primary.main : theme.palette.success[700],
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '4px !important',
              width: '40px',
              height: '32px',
              '&:hover': {
                backgroundColor: isSelected ? theme.palette.primary.dark : theme.palette.success[800],
              },
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                borderRadius: '4px !important',
                width: '40px',
                height: '32px',
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                },
              },
            }),
          }}
        />
      </Tooltip>
    );
  };

  const shouldDisableDateWithODS = (date: Dayjs): boolean => {
    const dateString = date.format('YYYY-MM-DD');
    const availability = odsAvailability?.[dateString];

    if (!availability) {
      return true;
    }

    return availability.availableStores === 0;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}
      adapterLocale={locale}>
      <DateCalendar
        value={value}
        onChange={onChange}
        sx={{
          maxWidth: '100%',
          '& .Mui-selected': {
            borderRadius: '4px !important',
            width: '40px',
            height: '32px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          '& .MuiPickersDay-root': {
            fontWeight: 'bold',
            borderRadius: '4px',
          },
          '& .Mui-disabled': {
            fontWeight: 'normal',
            color: 'lightgrey',
          },
          '& .MuiYearCalendar-root': {
            width: 'auto',
          },
        }}
        shouldDisableDate={(date) => shouldDisableDateWithODS(dayjs(date))}
        disabled={disabled}
        disableHighlightToday={disableHighlightToday}
        showDaysOutsideCurrentMonth
        minDate={minDate}
        maxDate={maxDate}
        slots={{ day: CustomDay }}
      />
    </LocalizationProvider>
  );
};

export default BulkOrderDateCalendar;
